import { MailArrowForwardRegular } from "@/pages/taskpane/components/icons/IconCustom";
import Button from "@/pages/taskpane/components/ui/Button";
import { useOutlookStore } from "@/pages/taskpane/lib/office/stores/outlookStore";
import { ISavePlaceOrderSessionDataRequest } from "@/pages/taskpane/lib/order/types/OrderEntity";
import { convertDateFormatToRc, convertTimeFormatToRc } from "@/pages/taskpane/lib/utils/dateTimeHelper";
import { generateCId } from "@/pages/taskpane/lib/utils/randomUUID";
import {
  useAppConfigStore,
  useAppointmentStore,
  useAttendeesStore,
  useResourceStore,
  useSettingStore,
  useThemeStore,
} from "@/pages/taskpane/shared/stores";
import { PreSelectedResourceInfo } from "@/pages/taskpane/shared/types/preSelectedResource";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EForceOpenOrder } from "../../resources/services/types/enum";
import { dateTimeToTicks } from "../../resources/ulti";
import { useOpenOrder } from "../hooks/useOpenOrder";

type Props = {
  notifyInfo?: PreSelectedResourceInfo;
};

const OpenOrderForm = ({ notifyInfo }: Props) => {
  const { isDarkMode } = useThemeStore();
  const { t } = useTranslation();

  // Service hook - container layer
  const { openPlaceOrder, onHandleCancel, onHandleRemoveItem } = useOpenOrder();

  // Gather data from stores
  const { startDateTimeUtc, endDateTimeUtc, timeZone } = useAppointmentStore();
  const { subject, iCalUId, customUid, recurringDates, globalId, guid } = useOutlookStore();
  const { language, meetingLabel } = useAppConfigStore();
  const { dateFormat, timeFormat } = useSettingStore();
  const { mainAttendee, requiredAttendees, optionalAttendees } = useAttendeesStore();
  const { meetingLabel: meetingLabelSelected, selectedResource } = useResourceStore();

  const attendees = useMemo(() => {
    return requiredAttendees.concat(optionalAttendees);
  }, [requiredAttendees, optionalAttendees]);

  const onRemoveItem = () => {
    if (notifyInfo) {
      onHandleRemoveItem(notifyInfo);
    }
  };

  const onOrder = () => {
    if (!notifyInfo || !notifyInfo.resources || notifyInfo.resources.length === 0) {
      return;
    }

    const sessionId = generateCId();
    // Build request object from stores data
    const request = {
      SelectedResources: notifyInfo.resources.map((r) => r.id || "").join(","),
      StartTime: dateTimeToTicks(new Date(startDateTimeUtc)).toString() || "",
      EndTime: dateTimeToTicks(new Date(endDateTimeUtc)).toString() || "",
      // OriginalEndTime: dateTimeToTicks(new Date(endDateTimeUtc)).toString() || "",
      // OriginalStartTime: dateTimeToTicks(new Date(startDateTimeUtc)).toString() || "",
      Subject: subject || "",
      OrganizerEmail: mainAttendee?.email || "",
      OrganizerName: mainAttendee?.name || "",
      TimeZoneCode: timeZone || "",
      CustomUid: customUid || "",
      Language: language || "en",
      GlobalId: globalId,
      Guid: guid,
      SessionId: sessionId,
      ReOpenOrderFormAfterSend: globalId ? "1" : "0",
      clientTimeFormat: convertTimeFormatToRc(timeFormat),
      clientDateFormat: convertDateFormatToRc(dateFormat),
      IsRecurringApp: recurringDates && recurringDates.length > 0 ? "1" : "0",
    };

    const requestSaveSession: ISavePlaceOrderSessionDataRequest = {
      SessionId: sessionId,
      Attendees: attendees.map((attendee) => ({ Email: attendee?.email || "", Name: attendee?.name || "" })),
      RecurringInstances:
        recurringDates && recurringDates.length > 0
          ? recurringDates.map((date) => dateTimeToTicks(new Date(date.startDate))).join(";")
          : "",
      ConflictResourcesOccurrencesMap: "",
      SelectedResourceIds: (selectedResource || [])?.map((r) => r.id),
      ExternalResourceIds: (selectedResource || []).filter((r) => r.isExternal).map((r) => r.id),
    };

    // Call service to open order form
    openPlaceOrder(requestSaveSession, request, notifyInfo, {
      meetingLabelSelected: meetingLabelSelected ?? 0,
      isNewMeeting: !iCalUId,
      meetingLabelOptions: meetingLabel?.meetingLabels ?? [],
      startDate: startDateTimeUtc || "",
      endDate: endDateTimeUtc || "",
    });
  };

  const onCancel = () => {
    if (notifyInfo) {
      onHandleCancel(notifyInfo);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-2">
        <div>
          <MailArrowForwardRegular isDarkMode={isDarkMode} />
        </div>
        <div className="flex flex-col gap-2 w-full">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{notifyInfo?.resources[0]?.name}</span>
          <span className="text-xs font-normal text-gray-900 dark:text-white">{t(notifyInfo?.message || "")}</span>
          <div className="flex justify-start mt-4 gap-3">
            {notifyInfo?.forceOpenOrder === EForceOpenOrder.None ? (
              <Button
                className="bg-primaryBlue text-white w-[50%] h-[32px] dark:bg-brightBlue dark:text-black text-xs"
                label="OK"
                onClick={onRemoveItem}
              />
            ) : (
              <>
                <Button
                  className="bg-primaryBlue text-white w-[100%] h-[32px] dark:bg-brightBlue dark:text-black text-xs"
                  label="Order"
                  onClick={onOrder}
                />
                <Button
                  className="bg-white dark:bg-darkBackgroundColor text-black border border-lightGray w-[100%] h-[32px] dark:text-white dark:border-darkMediumGray text-xs"
                  label="Cancel"
                  onClick={onCancel}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenOrderForm;
